/**
 * BottomSheet — 새 앨범 생성 옵션 시트
 * - 하단 슬라이드업 (framer-motion)
 * - Radix Dialog로 포커스 트랩 + Escape + scroll lock 자동 처리 (WCAG 2.1 AA)
 */
import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { bottomSheetVariants, fadeIn, useSnapVariants } from '../../lib/motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FilePlus, Palette, Upload, X } from 'lucide-react';
import { toast } from '../ui/Toast';
import * as Dialog from '@radix-ui/react-dialog';
import * as albumsApi from '../../api/albums.api';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  onOpenTemplate: () => void;
}

const BottomSheet: React.FC<BottomSheetProps> = ({ open, onClose, onOpenTemplate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const snapOverlay = useSnapVariants(fadeIn);
  const snapSheet = useSnapVariants(bottomSheetVariants);

  const handleBlankAlbum = () => {
    onClose();
    navigate('/albums/new');
  };

  const handleFromTemplate = () => {
    onClose();
    onOpenTemplate();
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset so same file can be re-selected
    e.target.value = '';

    if (file.size > 2 * 1024 * 1024) {
      toast({ title: t('albums.importSizeError'), description: '2MB 이하만 가져올 수 있습니다.', variant: 'destructive' });
      return;
    }

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const albumData: albumsApi.CreateAlbumData = {
        title: data.title || t('albums.importedAlbum'),
        artist: data.artist || '',
        tracklist: Array.isArray(data.tracklist) ? data.tracklist : [],
        printUnits: data.printUnits || {},
        year: data.year,
        genre: data.genre,
      };
      const created = await albumsApi.createAlbum(albumData);
      toast({ title: t('albums.importSuccess', { title: albumData.title }), variant: 'success' });
      onClose();
      navigate(`/albums/${created._id}/edit`);
    } catch {
      toast({ title: t('albums.importFailed'), description: t('albums.importFailedDesc'), variant: 'destructive' });
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            {/* 오버레이 */}
            <Dialog.Overlay asChild>
              <motion.div
                key="overlay"
                variants={snapOverlay}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="fixed inset-0 z-[60] bg-black/50"
              />
            </Dialog.Overlay>

            {/* 시트 */}
            <Dialog.Content
              asChild
              aria-describedby={undefined}
              onInteractOutside={onClose}
            >
              <motion.div
                key="sheet"
                variants={snapSheet}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="fixed bottom-0 left-0 right-0 z-[61] bg-white rounded-t-2xl shadow-2xl max-h-[70vh] overflow-auto"
                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
              >
                  {/* 핸들 */}
                  <div className="flex justify-center pt-2 pb-1">
                    <div className="w-10 h-1 bg-gray-300 rounded-full" />
                  </div>
                  {/* 타이틀 + 닫기 */}
                  <div className="px-4 pb-3 flex justify-between items-center">
                    <Dialog.Title className="text-base font-semibold text-gray-900">
                      {t('bottomSheet.newAlbum')}
                    </Dialog.Title>
                    <Dialog.Close asChild>
                      <button
                        aria-label={t('common.close')}
                        className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                      >
                        <X size={20} />
                      </button>
                    </Dialog.Close>
                  </div>

                  {/* 옵션 목록 */}
                  <div className="px-4 pb-6 space-y-2">
                    {/* 빈 앨범 */}
                    <button
                      onClick={handleBlankAlbum}
                      className="w-full flex items-center gap-4 px-4 py-4 rounded-xl hover:bg-blue-50 transition-colors text-left group"
                    >
                      <span className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 group-hover:bg-blue-200 transition-colors">
                        <FilePlus size={20} />
                      </span>
                      <div>
                        <p className="font-medium text-gray-900">{t('bottomSheet.blankAlbum')}</p>
                        <p className="text-sm text-gray-500">{t('bottomSheet.blankAlbumDesc')}</p>
                      </div>
                    </button>

                    {/* 템플릿에서 만들기 */}
                    <button
                      onClick={handleFromTemplate}
                      className="w-full flex items-center gap-4 px-4 py-4 rounded-xl hover:bg-purple-50 transition-colors text-left group"
                    >
                      <span className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 group-hover:bg-purple-200 transition-colors">
                        <Palette size={20} />
                      </span>
                      <div>
                        <p className="font-medium text-gray-900">{t('bottomSheet.fromTemplate')}</p>
                        <p className="text-sm text-gray-500">{t('bottomSheet.fromTemplateDesc')}</p>
                      </div>
                    </button>

                    {/* 가져오기 */}
                    <button
                      onClick={handleImport}
                      className="w-full flex items-center gap-4 px-4 py-4 rounded-xl hover:bg-green-50 transition-colors text-left group"
                    >
                      <span className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-600 group-hover:bg-green-200 transition-colors">
                        <Upload size={20} />
                      </span>
                      <div>
                        <p className="font-medium text-gray-900">{t('bottomSheet.import')}</p>
                        <p className="text-sm text-gray-500">{t('bottomSheet.importDesc')}</p>
                      </div>
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".json"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
};

export default BottomSheet;
